// TODO: evaluate the eslint settings for .eslintrc.js

/*eslint padding-line-between-statements: [
    "error",
    { blankLine: "always", prev: "function", next: "*" }
    { blankLine: "always", prev: "block", next: "*" }
    { blankLine: "always", prev: "export", next: "*" }
]*/
import { isCooktop, isRange } from '../utils';

import { QTECode, QTEType } from '~/core/constants';
import { ProductType } from '~/core/enums';
import {
  ApplianceModel,
  ApplianceType,
  OptionalProduct,
  PricedProduct,
  ProductDetails,
  QteTypes,
} from '~/core/models';

/**
 * @file this is where we put all logic to infer productType using the Estimate Reference Group data
 */
export const isOversizeFeeProduct = ({
  qteTypeId,
  qteCodeId,
}: QteTypes): boolean =>
  qteTypeId === QTEType.Other && qteCodeId === QTECode.OversizeFee;

export const isSmallProjectFee = ({
  qteTypeId,
  qteCodeId,
}: QteTypes): boolean =>
  qteTypeId === QTEType.Other && qteCodeId === QTECode.SmallProjectFee;

export const isSmallProjectColor = ({
  qteTypeId,
  qteCodeId,
}: QteTypes): boolean =>
  qteTypeId === QTEType.Material && qteCodeId === QTECode.SmallProjectFee;

export const isInsideDiagonalCornerProduct = ({
  qteTypeId,
  qteCodeId,
}: QteTypes): boolean =>
  qteTypeId === QTEType.Diagonal && qteCodeId === QTECode.InsideCorner;

export const isCustomWorkProduct = ({
  qteTypeId,
  qteCodeId,
}: QteTypes): boolean =>
  qteTypeId === QTEType.Other && qteCodeId === QTECode.CustomLabor;

export const isWarrantyProduct = ({
  qteTypeId,
  qteCodeId,
}: QteTypes): boolean =>
  qteTypeId === QTEType.Other && qteCodeId === QTECode.Warranty;

export const isOutsideDiagonalCornerProduct = ({
  qteTypeId,
  qteCodeId,
}: QteTypes): boolean =>
  qteTypeId === QTEType.Diagonal && qteCodeId !== QTECode.OutsideCorner;

export const isCookingApplianceProduct = (product: ApplianceModel): boolean =>
  isCooktop({ applianceModel: product }) ||
  isRange({ applianceModel: product });

export const isDishwasherProduct = (product: ApplianceModel): boolean =>
  product.applianceType === ApplianceType.DISHWASHER;

export const isSinkProduct = (product: ApplianceModel): boolean =>
  product.applianceType === ApplianceType.SINK;

export const isSinkLaborProduct = (product: ApplianceModel): boolean =>
  product.applianceType === ApplianceType.SINK_LABOR;

export const isRefrigeratorProduct = (product: ApplianceModel): boolean =>
  product.applianceType === ApplianceType.REFRIGERATOR;

export const isFaucetProduct = (product: ApplianceModel): boolean =>
  product.applianceType === ApplianceType.FAUCET;

export const isCornerRadiusProduct = (
  product: ProductDetails | QteTypes
): boolean => {
  const { qteTypeId, qteCodeId } = product;
  const productType = 'productType' in product ? product.productType : null;
  return (
    productType === ProductType.CORNER_RADIUS ||
    (!!qteCodeId &&
      qteTypeId === QTEType.Radius &&
      [QTECode.R1, QTECode.R2, QTECode.R3, QTECode.R4, QTECode.R5].includes(
        qteCodeId
      ))
  );
};

/**
 * is the given pricedProduct a countertop material or not?
 * @param product
 */
export const isCountertopProduct = (
  product: PricedProduct | QteTypes
): boolean =>
  /*
    product.qteTypeId === QTEType.Material is the new method and should be reliable,
    but until we've validated all the data, continue to support the older test
    (i.e. if it's not one of the other types, it must be a countertop)
     */
  product.qteTypeId === QTEType.Material ||
  ('productType' in product &&
    (product.productType === ProductType.COUNTERTOP ||
      !Object.values(ProductType).some((val) => product.productType === val)));

export const isCutoutProduct = ({ qteTypeId }: QteTypes): boolean =>
  qteTypeId === QTEType.Cutout;

export const isOptionalProduct = ({ qteTypeId }: QteTypes): boolean =>
  qteTypeId === QTEType.Other;

export const isTearOutProduct = ({ productType }: PricedProduct): boolean =>
  productType === ProductType.TEAR_OUT;

export const isEdgeProduct = ({ qteTypeId }: QteTypes): boolean =>
  qteTypeId === QTEType.Edge;

export const isAnyDiagonalCornerProduct = ({ qteTypeId }: QteTypes): boolean =>
  qteTypeId === QTEType.Diagonal;

export const isJointPointProduct = (_product: QteTypes): boolean => false; // we actually have no way of inferring this

export type GenericProduct = ProductDetails &
  ApplianceModel &
  PricedProduct &
  OptionalProduct;

/**
 * Identify the ProductType of a product-like object based on the product data
 * @param product
 * @returns the type of product, if no match then it should be a countertop
 */
export const getEEProductType = (product: GenericProduct): ProductType => {
  // order of these matters!
  const productTypeMap: [(product: GenericProduct) => boolean, ProductType][] =
    [
      [isSinkLaborProduct, ProductType.SINK_LABOR], // sinkLabor must be first
      [isCookingApplianceProduct, ProductType.COOKING_APPLIANCE], // then appliances
      [isDishwasherProduct, ProductType.DISHWASHER],
      [isRefrigeratorProduct, ProductType.REFRIGERATOR],
      [isSinkProduct, ProductType.SINK],
      [isFaucetProduct, ProductType.FAUCET],
      [isCornerRadiusProduct, ProductType.CORNER_RADIUS], //cornerRadius and CounterTop
      [isCountertopProduct, ProductType.COUNTERTOP],
      [isCustomWorkProduct, ProductType.CUSTOM_WORK], //Order of tests less important now
      [isCutoutProduct, ProductType.CUTOUT],
      [isTearOutProduct, ProductType.TEAR_OUT],
      [isEdgeProduct, ProductType.EDGE],
      [isJointPointProduct, ProductType.JOINT_POINT],
      [isOversizeFeeProduct, ProductType.OVERSIZE_FEE],
      [isWarrantyProduct, ProductType.WARRANTY],
      [isInsideDiagonalCornerProduct, ProductType.INSIDE_DIAGONAL],
      [isAnyDiagonalCornerProduct, ProductType.DIAGONAL],
      [isOptionalProduct, ProductType.OTHER], // isAnyOtherOptionalProduct should be after all specific Optional Products
    ];

  // TODO : implement a working test for other labor

  const match = productTypeMap.find(([checkFn]) => checkFn(product));
  return match ? match[1] : ProductType.COUNTERTOP;
};
